import { NJButton, NJIconButton } from '@engie-group/fluid-design-system-react';
import PrototypeStudioLayout from '@shared/PrototypeStudioLayout';
import {
    Size2MixAppShell,
    Size2MixNetworkIcon,
    TEST_PUC_NETWORK,
    useSize2MixRefNavigation,
    type NetworkListItem,
} from '@shared/size2mix-shell';
import { useHome, useRemoveFavorite } from './hooks';

export const meta = {
    title: "Référence — Accueil",
    description: "Page d'accueil de Nemo : Size2MixAppShell + rangées horizontales de cartes réseau (nj-card) (favoris, derniers créés, derniers modifiés) avec raccourcis.",
    tags: ['dashboard', 'network'],
    author: 'dv5886',
    status: 'review' as const,
    category: 'hors-reseau/home',
    createdAt: '2026-10-10',
};

const SHORTCUTS = [
    { icon: 'factory', label: 'Production', page: 'production' },
    { icon: 'insert_invitation', label: 'Plannings', page: 'plannings' },
    { icon: 'stacked_bar_chart', label: 'Optimisations', page: 'optimisations' },
] as const;

function NetworkCard({ network, onOpen, onRemove }: { network: NetworkListItem; onOpen: (page: 'general' | (typeof SHORTCUTS)[number]['page']) => void; onRemove?: () => void }) {
    return (
        <div className="nj-card" style={{ width: 260, minWidth: 260, height: 205, flexShrink: 0 }}>
            <div className="nj-card__body" style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minHeight: 32 }}>
                    <Size2MixNetworkIcon fluidType={network.fluidType} size={20} />
                    <a
                        href="#"
                        title={network.name}
                        style={{ flex: 1, fontSize: '0.8rem', lineHeight: '19px', padding: '4px 0', color: 'var(--nj-semantic-color-text-neutral-primary-default)', textDecoration: 'none', wordBreak: 'break-word' }}
                        onClick={(e) => {
                            e.preventDefault();
                            onOpen('general');
                        }}
                    >
                        {network.name}
                    </a>
                    {onRemove ? (
                        <NJIconButton icon="close" label="Retirer des favoris" variant="tertiary" scale="xs" onClick={onRemove} />
                    ) : (
                        <NJIconButton icon="favorite_border" label="Ajouter aux favoris" variant="tertiary" scale="xs" />
                    )}
                </div>
                {/* Nemo shows a map thumbnail of the network here. */}
                <div
                    aria-hidden
                    style={{
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--nj-semantic-color-background-neutral-secondary-default)',
                        color: 'var(--nj-core-color-grey-300)',
                    }}
                >
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 'auto' }}>
                    <span style={{ flex: 1, fontSize: 12.8, lineHeight: '16px', whiteSpace: 'nowrap', color: 'var(--nj-semantic-color-text-neutral-tertiary-default)' }}>{network.updatedAt}</span>
                    {SHORTCUTS.map((s) => (
                        <NJIconButton key={s.icon} icon={s.icon} label={s.label} variant="tertiary" scale="xs" onClick={() => onOpen(s.page)} />
                    ))}
                </div>
            </div>
        </div>
    );
}

function CardRow({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
    return (
        <section style={{ margin: '0 0 52px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: action ? 42 : 40, marginBottom: action ? 18 : 0 }}>
                <h4 style={{ margin: 0, fontWeight: 400, fontSize: 24, lineHeight: '40px' }}>{title}</h4>
                {action}
            </div>
            <div style={{ display: 'flex', gap: 20, overflowX: 'auto' }}>{children}</div>
        </section>
    );
}

export default function RefHome() {
    const nav = useSize2MixRefNavigation();
    const { data } = useHome();
    const removeFavorite = useRemoveFavorite();

    const openNetwork = (network: NetworkListItem) => (page: 'general' | 'production' | 'plannings' | 'optimisations') => {
        if (network.eid === TEST_PUC_NETWORK.eid) nav.goTo(page);
    };

    const cards = (items: NetworkListItem[], removable = false) =>
        items.map((n) => <NetworkCard key={n.eid} network={n} onOpen={openNetwork(n)} onRemove={removable ? () => removeFavorite.mutate(n.eid) : undefined} />);

    return (
        <PrototypeStudioLayout title={meta.title}>
            <Size2MixAppShell
                activePrimary="contracts"
                onPrimaryNavigate={nav.onPrimaryNavigate}
            >
                {data && (
                    <div style={{ paddingTop: 28 }}>
                        <CardRow title={`Mes réseaux favoris (${data.favorites.total})`}>{cards(data.favorites.items, true)}</CardRow>
                        <CardRow title={`Mes derniers réseaux créés (${data.lastCreated.length})`} action={<NJButton label="Nouveau réseau" onClick={() => nav.goTo('networkList')} />}>{cards(data.lastCreated)}</CardRow>
                        <CardRow title={`Les derniers réseaux modifiés dans mon entité (${data.lastUpdatedInEntity.length})`}>{cards(data.lastUpdatedInEntity)}</CardRow>
                        <a href="#" className="nj-link" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }} onClick={(event) => { event.preventDefault(); nav.goTo('networkList'); }}><span className="material-icons" aria-hidden="true">add</span>Voir tous les réseaux</a>
                    </div>
                )}
            </Size2MixAppShell>
        </PrototypeStudioLayout>
    );
}
